package one.healthium.insights.data

import co.touchlab.kermit.Logger
import dev.gitlive.firebase.functions.FirebaseFunctions
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.flow
import kotlinx.serialization.modules.SerializersModule
import one.healthium.insights.data.dto.ContentScreenDto
import one.healthium.insights.data.mapper.ContentScreenMapper
import one.healthium.insights.data.sources.network.ContentApiClient
import one.healthium.insights.data.sources.storage.SavedContentStorage
import one.healthium.insights.data.utils.CustomSerializerModules
import one.healthium.insights.domain.content.ContentOptions
import one.healthium.insights.domain.content.ContentRepository
import one.healthium.insights.domain.models.ContentScreenModel
import one.healthium.insights.domain.models.SavedContentModel
import one.healthium.insights.domain.models.SavedContentOptions
import one.healthium.meal_plans.domain.MealPlanInfoModel

internal class AppContentRepository(
    private val apiClient: ContentApiClient,
    private val contentScreenMapper: ContentScreenMapper,
    private val customSerializerModules: CustomSerializerModules,
    private val savedContentStorage: SavedContentStorage,
    private val functions: FirebaseFunctions,
    private val localeProvider: one.healthium.locale.LocaleProvider,
) : ContentRepository {

    private val cache = mutableMapOf<ContentOptions, ContentScreenModel>()

    override fun getContent(options: ContentOptions, force: Boolean): Flow<ContentScreenModel> {
        return flow {
            val cachedValue = if (force) null else cache[options]
            if (cachedValue != null) {
                emit(cachedValue)
            }
            val locale = localeProvider.getLocale()
            val contentDetailsDto = when (options) {
                is ContentOptions.Category -> apiClient.getScreenModel(options.id, locale)
                is ContentOptions.Insight -> apiClient.getInsight(options.id, locale)
                is ContentOptions.Recipe -> apiClient.getRecipe(options.id, locale)
                is ContentOptions.MealPlan -> {
                    if (options.id == MealPlanInfoModel.PERSONAL_ID) {
                        if (cachedValue != null) {
                            return@flow
                        }

                        loadPersonalMealPlan(force)
                    } else {
                        apiClient.getMealPlan(options.id, locale)
                    }
                }

                is ContentOptions.Workout -> apiClient.getWorkout(options.id, locale)
            }
            Logger.d("Fetched content from network: $contentDetailsDto")
            val contentDetailModel = contentScreenMapper.dtoToModel(contentDetailsDto)
            cache[options] = contentDetailModel
            emit(contentDetailModel)
            saveRecentlyWatched(options, contentDetailModel)
        }.distinctUntilChanged()
    }

    override fun getFavoriteContent(): Flow<List<SavedContentModel>> {
        return savedContentStorage.getFavorites()
    }

    override suspend fun addContentToFavorite(options: SavedContentOptions) {
        savedContentStorage.addFavorite(options)
    }

    override suspend fun removeContentFromFavorite(id: String) {
        savedContentStorage.remove(id)
    }

    override fun getRecentlyContent(): Flow<List<SavedContentModel>> {
        return savedContentStorage.getRecently()
    }

    private suspend fun loadPersonalMealPlan(force: Boolean): ContentScreenDto {
        val contentScreenDto = functions
            .httpsCallable("get_mealplan")
            .invoke(mapOf("regenerate_mealplan" to force))
            .data(ContentScreenDto.serializer()) {
                serializersModule = SerializersModule {
                    customSerializerModules.modules.forEach { include(it) }
                }
            }
        return contentScreenDto
    }

    private suspend fun saveRecentlyWatched(contentOptions: ContentOptions, contentScreenModel: ContentScreenModel) {
        try {
            val preview = contentScreenModel.preview ?: return
            val savedContentOptions = SavedContentOptions(
                contentOptions = contentOptions,
                contentPreview = preview,
            )
            savedContentStorage.addRecently(savedContentOptions)
            savedContentStorage.deleteOldRecentlyEntries()
        } catch (e: Exception) {
            Logger.e("Error saving recently watched content", e)
        }
    }
}